import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import modernTheme from '@/theme/modernTheme';
import {
  Box,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CalendarToday,
  Menu,
  Close,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import api from '@/services/api';

const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';
const pill = { borderRadius: '999px', textTransform: 'none' as const, fontFamily: FONT, fontWeight: 600, px: 3.5 };

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Features', path: '/features' },
  { label: 'Pricing', path: '/pricing' },
];

const PublicLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await api.get('/auth/signup-status');
        setSignupsEnabled(r.data.signupsEnabled);
      } catch {
        setSignupsEnabled(true);
      }
    };
    check();
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <ThemeProvider theme={modernTheme}>
    <CssBaseline />
    <Box sx={{ minHeight: '100vh', fontFamily: FONT }}>
      {/* ─── Frosted navbar ─────────────────────────────────────── */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(16px) saturate(180%)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          color: 'text.primary',
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ maxWidth: 1200, width: '100%', mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: ACCENT }}>
              <CalendarToday sx={{ fontSize: 20 }} />
            </Avatar>
            <Box component="span" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.1rem' }}>
              Player Central
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 5, flexGrow: 1 }}>
            {NAV_LINKS.map(({ label, path }) => (
              <Button
                key={label}
                sx={{
                  fontFamily: FONT, fontWeight: isActive(path) ? 600 : 500, textTransform: 'none',
                  color: isActive(path) ? 'text.primary' : 'text.secondary',
                  borderRadius: 2, px: 2,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: 'text.primary' },
                }}
                onClick={() => {
                  if (path === '/' && location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
                  else navigate(path);
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            <Button
              sx={{ ...pill, color: 'text.primary', border: '1px solid rgba(0,0,0,0.12)', '&:hover': { borderColor: ACCENT, color: ACCENT } }}
              onClick={() => navigate('/login')}
            >
              Log in
            </Button>
            {signupsEnabled && (
              <Button
                variant="contained"
                sx={{ ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, boxShadow: 'none' }}
                onClick={() => navigate('/register')}
              >
                Sign up
              </Button>
            )}
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />
          <IconButton
            edge="end"
            sx={{ display: { xs: 'flex', md: 'none' } }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <Close /> : <Menu />}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        anchor="right"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, fontFamily: FONT },
        }}
      >
        <Toolbar />
        <List>
          {NAV_LINKS.map(({ label, path }) => (
            <ListItem
              key={label}
              button
              onClick={() => {
                if (path === '/' && location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
                else navigate(path);
                setMobileMenuOpen(false);
              }}
            >
              <ListItemText primary={label} primaryTypographyProps={{ fontFamily: FONT, fontWeight: isActive(path) ? 600 : 400 }} />
            </ListItem>
          ))}
          <ListItem>
            <Button fullWidth sx={{ ...pill, border: '1px solid rgba(0,0,0,0.12)', mb: 1 }} onClick={() => navigate('/login')}>
              Log in
            </Button>
          </ListItem>
          {signupsEnabled && (
            <ListItem>
              <Button fullWidth variant="contained" sx={{ ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, boxShadow: 'none' }} onClick={() => navigate('/register')}>
                Sign up
              </Button>
            </ListItem>
          )}
        </List>
      </Drawer>

      {/* Page content */}
      <Outlet />
    </Box>
    </ThemeProvider>
  );
};

export default PublicLayout;
